import { faker } from '@faker-js/faker';
import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

export interface GetBatchesParams {
  grade: string;
  product_id: string;
  profile_id?: string;
  variant_id?: string;
}

export interface BatchSessionInfo {
  schedule: Array<{
    day: string;
    time: string;
  }>;
}

export interface NaplanBatch {
  batch_id: string;
  variant_id: string;
  start_date: string;
  course_session_info: BatchSessionInfo;
  [key: string]: unknown;
}

export interface GetBatchesResponse {
  status: string;
  message: string;
  batches: NaplanBatch[];
}

/**
 * Fetch batches for NAPLAN enrollment
 * Based on the reference implementation from starry-night
 */
export const getBatches = async (
  params: GetBatchesParams
): Promise<GetBatchesResponse> => {
  const { grade, product_id, profile_id, variant_id } = params;

  // Build query parameters
  const queryParams: Record<string, string> = {
    grade,
    product_id,
  };

  if (profile_id) {
    queryParams.profile_id = profile_id;
  }

  if (variant_id) {
    queryParams.variant_id = variant_id;
  }

  const response = await axios.get<GetBatchesResponse>(
    '/streams/v1/variant/batch',
    {
      params: queryParams,
    }
  );

  return response;
};

/**
 * Generate mock NAPLAN batches for testing
 */
const generateMockBatches = (count: number = 3): NaplanBatch[] => {
  return Array.from({ length: count }, () => ({
    batch_id: faker.string.uuid(),
    variant_id: faker.string.uuid(),
    start_date: faker.date.future().toISOString(),
    course_session_info: {
      schedule: [
        {
          day: faker.helpers.arrayElement([
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday',
            'Sunday',
          ]),
          time: faker.helpers.arrayElement([
            '9:00 AM',
            '10:00 AM',
            '11:00 AM',
            '2:00 PM',
            '3:00 PM',
            '4:00 PM',
            '5:00 PM',
          ]),
        },
      ],
    },
  }));
};

/**
 * MSW handler for fetching NAPLAN batches
 */
export const getBatchesMockHandler = http.get(
  `${env.API_URL}/streams/v1/variant/batch`,
  async ({ request }) => {
    await networkDelay(); // Realistic network delay

    const url = new URL(request.url);
    const grade = url.searchParams.get('grade');
    const product_id = url.searchParams.get('product_id');
    const profile_id = url.searchParams.get('profile_id');
    const variant_id = url.searchParams.get('variant_id');

    if (variant_id && !grade) {
      return void 0;
    }

    if (!grade || !product_id || !profile_id) {
      return HttpResponse.json(
        {
          status: 'error',
          message:
            'Missing required parameters: grade, product_id, or profile_id',
          batches: [],
        },
        { status: 400 }
      );
    }

    // Generate mock batches
    const batches = generateMockBatches(3);

    return HttpResponse.json(
      {
        status: 'success',
        message: 'Batches fetched successfully',
        batches,
      },
      { status: 200 }
    );
  }
);
